import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { TranslateButton } from '../../components/admin/TranslateButton';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, GripVertical, ExternalLink, Loader2, Sparkles, User, Gift, Trophy, Calendar, Image } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ICON_OPTIONS = [
  { value: 'Sparkles', label: 'Sparkles', icon: Sparkles },
  { value: 'User', label: 'User', icon: User },
  { value: 'Gift', label: 'Gift', icon: Gift },
  { value: 'Trophy', label: 'Trophy', icon: Trophy },
  { value: 'Calendar', label: 'Calendar', icon: Calendar },
];

const getIconComponent = (iconName) => {
  const found = ICON_OPTIONS.find(i => i.value === iconName);
  return found ? found.icon : Sparkles;
};

const SortableCTARow = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="p-4 bg-white" {...attributes}>
      {children(listeners || {})}
    </div>
  );
};

const AdminCTAs = () => {
  const { authFetch } = useAuth();
  const [ctas, setCTAs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: { ca: '', es: '', fr: '', en: '' },
    icon: 'Sparkles',
    image: '',
    link: '',
    external: false,
    is_active: true
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ctas.findIndex(c => c.id === active.id);
    const newIndex = ctas.findIndex(c => c.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(ctas, oldIndex, newIndex);
    setCTAs(reordered);
    try {
      const response = await authFetch(`${API_URL}/api/admin/ctas/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: reordered.map(c => c.id) })
      });
      if (!response.ok) throw new Error('reorder failed');
      toast.success('Ordre actualitzat');
    } catch (err) {
      toast.error('Error reordenant CTAs');
      fetchCTAs();
    }
  };

  useEffect(() => {
    fetchCTAs();
  }, []);

  const fetchCTAs = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/ctas`, {  });
      if (response.ok) {
        const data = await response.json();
        setCTAs(data.ctas || []);
      }
    } catch (error) {
      console.error('Failed to fetch CTAs:', error);
    } finally {
      setLoading(false);
    }
  };

  const initDefaultCTAs = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/init_ctas`, {
        method: 'POST',
        
      });
      if (response.ok) {
        toast.success('CTAs inicialitzats correctament');
        fetchCTAs();
      }
    } catch (error) {
      toast.error('Error inicialitzant CTAs');
    }
  };

  const handleEdit = (cta) => {
    setEditing(cta.id);
    setFormData({
      title: cta.title || { ca: '', es: '', fr: '', en: '' },
      icon: cta.icon || 'Sparkles',
      image: cta.image || '',
      link: cta.link || '',
      external: cta.external || false,
      is_active: cta.is_active !== false
    });
  };

  const handleSave = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/ctas/${editing}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('CTA actualitzat');
        setEditing(null);
        fetchCTAs();
      } else {
        toast.error('Error actualitzant CTA');
      }
    } catch (error) {
      toast.error('Error de connexió');
    }
  };

  const handleDelete = async (ctaId) => {
    if (!window.confirm('Segur que vols eliminar aquest CTA?')) return;

    try {
      const response = await authFetch(`${API_URL}/api/admin/ctas/${ctaId}`, {
        method: 'DELETE',
        
      });

      if (response.ok) {
        toast.success('CTA eliminat');
        fetchCTAs();
      } else {
        toast.error('Error eliminant CTA');
      }
    } catch (error) {
      toast.error('Error de connexió');
    }
  };

  const handleCreate = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/ctas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('CTA creat');
        setEditing(null);
        setFormData({
          title: { ca: '', es: '', fr: '', en: '' },
          icon: 'Sparkles',
          image: '',
          link: '',
          external: false,
          is_active: true
        });
        fetchCTAs();
      } else {
        toast.error('Error creant CTA');
      }
    } catch (error) {
      toast.error('Error de connexió');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-ctas-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[#1A1A1A]">
              Gestió CTAs Flotants
            </h1>
            <p className="text-sm text-[#666666] mt-1">
              Administra els botons "Prova el Golf" de la pàgina principal
            </p>
          </div>
          {ctas.length === 0 ? (
            <Button onClick={initDefaultCTAs} className="bg-[#1A4D2E] hover:bg-[#153d24] text-white">
              <Plus className="w-4 h-4 mr-2" />
              Inicialitzar CTAs per defecte
            </Button>
          ) : (
            <Button 
              onClick={() => setEditing('new')} 
              className="bg-[#1A4D2E] hover:bg-[#153d24] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nou CTA
            </Button>
          )}
        </div>

        {/* CTA List */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
          {ctas.length === 0 ? (
            <div className="p-8 text-center text-[#666666]">
              <Image className="w-12 h-12 mx-auto mb-4 text-[#CCCCCC]" />
              <p>No hi ha CTAs configurats</p>
              <p className="text-sm mt-2">Clica "Inicialitzar CTAs per defecte" per començar</p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={ctas.map(c => c.id)} strategy={verticalListSortingStrategy}>
                <div className="divide-y divide-[#E5E5E5]">
                  {ctas.map((cta) => {
                    const IconComp = getIconComponent(cta.icon);
                    const isEditing = editing === cta.id;
                    return (
                      <SortableCTARow key={cta.id} id={cta.id} disabled={isEditing}>
                        {(dragHandleProps) => (
                          isEditing ? (
                      /* Edit Form */
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <Label>Títol (CA)</Label>
                              <TranslateButton
                                text={formData.title.ca}
                                sourceLanguage="ca"
                                onTranslated={(translations) => setFormData({
                                  ...formData,
                                  title: { ...formData.title, ...translations }
                                })}
                              />
                            </div>
                            <Input
                              value={formData.title.ca}
                              onChange={(e) => setFormData({
                                ...formData,
                                title: { ...formData.title, ca: e.target.value }
                              })}
                              placeholder="Prova el Golf"
                            />
                          </div>
                          <div>
                            <Label>Títol (ES)</Label>
                            <Input
                              value={formData.title.es}
                              onChange={(e) => setFormData({
                                ...formData,
                                title: { ...formData.title, es: e.target.value }
                              })}
                              placeholder="Prueba el Golf"
                            />
                          </div>
                          <div>
                            <Label>Títol (FR)</Label>
                            <Input
                              value={formData.title.fr}
                              onChange={(e) => setFormData({
                                ...formData,
                                title: { ...formData.title, fr: e.target.value }
                              })}
                              placeholder="Essayez le Golf"
                            />
                          </div>
                          <div>
                            <Label>Títol (EN)</Label>
                            <Input
                              value={formData.title.en}
                              onChange={(e) => setFormData({
                                ...formData,
                                title: { ...formData.title, en: e.target.value }
                              })}
                              placeholder="Try Golf"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Icona</Label>
                            <select
                              value={formData.icon}
                              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                              className="w-full h-10 px-3 border border-[#E5E5E5] rounded-md"
                            >
                              {ICON_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <Label>URL Imatge</Label>
                            <Input
                              value={formData.image}
                              onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                              placeholder="https://..."
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Enllaç</Label>
                            <Input
                              value={formData.link}
                              onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                              placeholder="/iniciacio o https://..."
                            />
                          </div>
                          <div className="flex items-center gap-6 pt-6">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={formData.external}
                                onCheckedChange={(checked) => setFormData({ ...formData, external: checked })}
                              />
                              <Label>Enllaç extern</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={formData.is_active}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                              />
                              <Label>Actiu</Label>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2 pt-2">
                          <Button onClick={handleSave} className="bg-[#1A4D2E] hover:bg-[#153d24] text-white">
                            Guardar
                          </Button>
                          <Button variant="outline" onClick={() => setEditing(null)}>
                            Cancel·lar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* Display Mode */
                      <div className="flex items-center gap-4">
                        <button
                          type="button"
                          className="touch-none cursor-grab active:cursor-grabbing"
                          aria-label="Arrossegar per reordenar"
                          {...dragHandleProps}
                        >
                          <GripVertical className="w-5 h-5 text-[#CCCCCC]" />
                        </button>
                        
                        {/* Preview image */}
                        <div className="w-16 h-12 rounded overflow-hidden bg-[#F5F5F5] flex-shrink-0">
                          {cta.image ? (
                            <img src={cta.image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Image className="w-6 h-6 text-[#CCCCCC]" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <IconComp className="w-4 h-4 text-[#1A4D2E]" />
                            <span className="font-medium text-[#1A1A1A]">
                              {cta.title?.ca || 'Sense títol'}
                            </span>
                            {cta.external && (
                              <ExternalLink className="w-3 h-3 text-[#888888]" />
                            )}
                            {!cta.is_active && (
                              <span className="text-xs bg-[#FEE2E2] text-[#DC2626] px-2 py-0.5 rounded">
                                Inactiu
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#666666] truncate">
                            {cta.link}
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(cta)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(cta.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )
                        )}
                      </SortableCTARow>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* New CTA Form */}
        {editing === 'new' && (
          <div className="bg-white border border-[#1A4D2E] rounded-lg p-4 space-y-4">
            <h3 className="font-medium text-[#1A1A1A]">Nou CTA</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Títol (CA)</Label>
                  <TranslateButton
                    text={formData.title.ca}
                    sourceLanguage="ca"
                    onTranslated={(translations) => setFormData({
                      ...formData,
                      title: { ...formData.title, ...translations }
                    })}
                  />
                </div>
                <Input
                  value={formData.title.ca}
                  onChange={(e) => setFormData({
                    ...formData,
                    title: { ...formData.title, ca: e.target.value }
                  })}
                />
              </div>
              <div>
                <Label>Títol (ES)</Label>
                <Input
                  value={formData.title.es}
                  onChange={(e) => setFormData({
                    ...formData,
                    title: { ...formData.title, es: e.target.value }
                  })}
                />
              </div>
              <div>
                <Label>Títol (FR)</Label>
                <Input
                  value={formData.title.fr}
                  onChange={(e) => setFormData({
                    ...formData,
                    title: { ...formData.title, fr: e.target.value }
                  })}
                />
              </div>
              <div>
                <Label>Títol (EN)</Label>
                <Input
                  value={formData.title.en}
                  onChange={(e) => setFormData({
                    ...formData,
                    title: { ...formData.title, en: e.target.value }
                  })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Icona</Label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full h-10 px-3 border border-[#E5E5E5] rounded-md"
                >
                  {ICON_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>URL Imatge</Label>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Enllaç</Label>
                <Input
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-6 pt-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.external}
                    onCheckedChange={(checked) => setFormData({ ...formData, external: checked })}
                  />
                  <Label>Enllaç extern</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label>Actiu</Label>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={handleCreate} className="bg-[#1A4D2E] hover:bg-[#153d24] text-white">
                Crear CTA
              </Button>
              <Button variant="outline" onClick={() => setEditing(null)}>
                Cancel·lar
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCTAs;
